import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { invoiceColumns } from '@Constants/TableColumns';
import { type ApiInvoice, EnumFrontendLayoutSetting, EnumInvoiceType, type LinkableField } from '@Model';
import {
    type AsyncStateUpdate,
    Card,
    Grid,
    GridColumn,
    type IGuiBinder,
    isLinked,
    useLinkedField,
    useLinkProperty,
    useTranslate,
} from '@synqo/frontend-component-library';
import { memo } from 'react';
import { FrameEditInvoice } from '../../FrameEditInvoice';

// @ai-generated 2026-09-18T07:55:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5

interface IProps {
    binder: IGuiBinder<ApiInvoice>;
    baseApiObjectPath: string;
    requestUpdateState: AsyncStateUpdate<ApiInvoice>;

    type: EnumInvoiceType;
    relatedInvoice: LinkableField;
    relatedCredit: LinkableField;
}

export const InvoiceRelatedVouchersSection = memo(function InvoiceRelatedVouchersSection({
    binder,
    baseApiObjectPath,
    requestUpdateState,
    type,
    relatedInvoice,
    relatedCredit,
}: IProps) {
    const i18n = useTranslate('');

    const linkRelatedInvoice = useLinkedField<ApiInvoice, ApiInvoice>(
        baseApiObjectPath,
        'related_invoice',
        FrameEditInvoice,
        true,
        requestUpdateState,
    );
    const linkRelatedCredit = useLinkedField<ApiInvoice, ApiInvoice>(
        baseApiObjectPath,
        'related_credit',
        FrameEditInvoice,
        true,
        requestUpdateState,
    );

    const invoiceField = useLinkProperty(binder, linkRelatedInvoice.linkApi);
    const creditField = useLinkProperty(binder, linkRelatedCredit.linkApi);

    const showInvoiceLink = type === EnumInvoiceType.CreditNote || isLinked(relatedInvoice);
    const showCreditLink = type === EnumInvoiceType.Invoice || isLinked(relatedCredit);

    return (
        <Card name="related_vouchers">
            <Grid>
                {showInvoiceLink && (
                    <GridColumn span={6}>
                        <LinkedFieldWithLayoutSettings<ApiInvoice>
                            layoutSettingsContext={EnumFrontendLayoutSetting.InvoicesTableLayout}
                            {...invoiceField}
                            {...linkRelatedInvoice.events}
                            columns={invoiceColumns}
                            label={i18n('Rechnung')}
                        />
                    </GridColumn>
                )}
                {showCreditLink && (
                    <GridColumn span={6}>
                        <LinkedFieldWithLayoutSettings<ApiInvoice>
                            layoutSettingsContext={EnumFrontendLayoutSetting.InvoicesTableLayout}
                            {...creditField}
                            {...linkRelatedCredit.events}
                            columns={invoiceColumns}
                            label={i18n('Gutschrift')}
                        />
                    </GridColumn>
                )}
            </Grid>
        </Card>
    );
});
